/**
 * useMetaAttributionData.ts
 *
 * Fetches Meta-attributed application counts by cross-matching optins
 * (from the selected date range) to the applications collection via
 * email → phone → name fallback matching.
 *
 * This is a best-effort attribution until UTM tracking is set up in Meta Ads Manager.
 */

import { useState, useEffect, useRef } from "react";

export interface MetaAttributionResult {
  current:  number;
  previous: number;
  loading:  boolean;
  error:    string | null;
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function useMetaAttributionData(
  startDate: string,
  endDate:   string,
): MetaAttributionResult {
  const [current,  setCurrent]  = useState(0);
  const [previous, setPrevious] = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const startMs  = new Date(startDate + "T00:00:00Z").getTime();
    const endMs    = new Date(endDate   + "T00:00:00Z").getTime();
    const duration = Math.round((endMs - startMs) / 86_400_000) + 1;
    const prevEnd   = addDays(startDate, -1);
    const prevStart = addDays(prevEnd, -(duration - 1));

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ startDate, endDate, prevStart, prevEnd });

    fetch(`/api/meta-attribution?${params}`, { signal: ctrl.signal })
      .then(r => r.json() as Promise<{ current: number; previous: number }>)
      .then(data => {
        setCurrent(data.current);
        setPrevious(data.previous);
        setLoading(false);
      })
      .catch(err => {
        if ((err as Error).name === "AbortError") return;
        setError(String(err));
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [startDate, endDate]);

  return { current, previous, loading, error };
}
